import { useCallback, useEffect, useRef, useState, useMemo } from 'react';
import { v4 } from 'uuid';
import * as google from './utils';

export const polygonOptions = {
  fillColor: `#ffff00`,
  fillOpacity: 0.5,
  strokeWeight: 2,
  clickable: false,
  editable: true,
  zIndex: 998,
};

export const polylineOptions = {
  strokeColor: `#00ffff`,
  strokeWeight: 5,
  zIndex: 999,
};

export function useDrawingManager(map) {
  const polygons = useRef({});
  const [manager, setManager] = useState();

  useEffect(() => {
    if (manager || !map) return;

    window.google.maps.importLibrary('drawing').then(({ DrawingManager }) => {
      setManager(
        new DrawingManager({
          map,
          drawingControl: false,
          polygonOptions,
          polylineOptions,
        }),
      );
    });
  }, [manager, map]);

  const listenFor = useCallback(
    (event, callback) => {
      const listener = google.addListener(manager, event, callback);
      return () => google.removeListener(listener);
    },
    [manager],
  );

  const setMode = useCallback(
    (mode) => manager.setDrawingMode(mode),
    [manager],
  );

  const deregisterPolygon = useCallback((id) => {
    const { polygon, listeners } = polygons.current[id];
    polygon.setMap(null);
    delete polygons.current[id];
    listeners.forEach(google.removeListener);
  }, []);

  const deregisterAllPolygons = useCallback(() => {
    Object.keys(polygons.current).forEach(deregisterPolygon);
  }, [deregisterPolygon]);

  const toGeoJson = useCallback(() => {
    const dataLayer = new window.google.maps.Data();

    Object.entries(polygons.current).forEach(([id, { polygon }]) => {
      dataLayer.add(google.polygonToFeature(polygon, id));
    });

    return new Promise((resolve) => {
      dataLayer.toGeoJson(resolve);
    });
  }, []);

  const registerPolygon = useCallback((polygon, listeners) => {
    const id = v4();
    polygon.set('id', id);
    polygons.current[id] = { polygon, listeners };
  }, []);

  return useMemo(
    () =>
      manager
        ? {
            registerPolygon,
            listenFor,
            deregisterPolygon,
            deregisterAllPolygons,
            setMode,
            toGeoJson,
          }
        : null,
    [
      registerPolygon,
      listenFor,
      manager,
      deregisterPolygon,
      deregisterAllPolygons,
      setMode,
      toGeoJson,
    ],
  );
}
